import React, { useState, useRef, useEffect } from 'react';
import { uploadFileToS3, listFilesFromS3 } from '../../services/s3Upload';

const FileUpload = ({ 
  onUploadSuccess, 
  onUploadError, 
  accept = 'image/*',
  maxSize = 5000 * 1024 * 1024, // 5000MB
  folder = 'thumbnails',
  className = '',
  disabled = false,
  showPreview = true,
  currentUrl = null,
  aws_url = null,
  showFileBrowser = true, // New prop to enable/disable file browser
  onFileSelect = null, // Callback for when existing file is selected
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [preview, setPreview] = useState(currentUrl);
  const [dragOver, setDragOver] = useState(false);
  const [showBrowser, setShowBrowser] = useState(false);
  const [existingFiles, setExistingFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const fileInputRef = useRef(null);

  const validateFile = (file) => {
    if (file.size > maxSize) {
      throw new Error(`File size must be less than ${Math.round(maxSize / 1024 / 1024)}MB`);
    }

    if (accept && !file.type.match(accept.replace('*', '.*'))) {
      throw new Error(`File type ${file.type} not allowed`);
    }

    return true;
  };

  // Load existing files from S3
  const loadExistingFiles = async () => {
    if (!showFileBrowser) return;
    
    try {
      setLoadingFiles(true);
      const result = await listFilesFromS3(folder);
      if (result.success) {
        setExistingFiles(result.files);
      }
    } catch (error) {
      console.error('Error loading existing files:', error);
      onUploadError && onUploadError('Failed to load existing files');
    } finally {
      setLoadingFiles(false);
    }
  };

  // Handle selection of existing file
  const handleExistingFileSelect = (file) => {
    setPreview(file.url);
    setShowBrowser(false);
    
    // Call the onFileSelect callback if provided
    if (onFileSelect) {
      onFileSelect(file.url, file.key);
    }
    
    // Also call onUploadSuccess for backward compatibility
    onUploadSuccess && onUploadSuccess(file.url, file.key);
  };

  const handleFileSelect = async (file) => {
    if (!file) return;

    try {
      setUploading(true);
      setUploadProgress(0);
      validateFile(file);

      // Create preview for images
      if (showPreview && file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => setPreview(e.target.result);
        reader.readAsDataURL(file);
      }

      // Handle upload progress
      const handleProgress = (percentage, loaded, total) => {
        setUploadProgress(percentage);
      };

      const result = await uploadFileToS3(file, folder, handleProgress);
      
      if (result.success) {
        console.log("file url", result.url);
        setPreview(result.url);
        setUploadProgress(100);
        aws_url = result.url;
        
        onUploadSuccess && onUploadSuccess(result.url, result.key);
      }
    } catch (error) {
      console.error('Upload error:', error);
      onUploadError && onUploadError(error.message);
      setPreview(currentUrl);
    } finally {
      setUploading(false);
      // Reset progress after a short delay
      setTimeout(() => setUploadProgress(0), 500);
    }
  };

  const handleInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const openFileDialog = () => {
    if (!disabled && !uploading) {
      fileInputRef.current?.click();
    }
  };

  const toggleFileBrowser = () => {
    if (!showFileBrowser) return;
    
    if (!showBrowser && existingFiles.length === 0) {
      loadExistingFiles();
    }
    setShowBrowser(!showBrowser);
  };

  return (
    <div className={`relative ${className}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleInputChange}
        className="hidden"
        disabled={disabled || uploading}
      />

      {/* File Browser Modal */}
      {showBrowser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[80vh] overflow-hidden">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Select from existing files</h3>
              <button
                type="button"
                onClick={() => setShowBrowser(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {loadingFiles ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-600">Loading files...</span>
              </div>
            ) : existingFiles.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No files found in this folder
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-h-96 overflow-y-auto">
                {existingFiles.map((file, index) => (
                  <div
                    key={index}
                    onClick={() => handleExistingFileSelect(file)}
                    className="border rounded-lg p-2 cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-all"
                  >
                    {file.name.match(/\.(jpg|jpeg|png|gif|svg)$/i) ? (
                      <img
                        src={file.url}
                        alt={file.name}
                        className="w-full h-24 object-cover rounded mb-2"
                      />
                    ) : (
                      <div className="w-full h-24 bg-gray-100 rounded mb-2 flex items-center justify-center">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                    )}
                    <p className="text-xs text-gray-600 truncate" title={file.name}>
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-400">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Upload Actions */}
      {showFileBrowser && (
        <div className="flex gap-2 mb-2">
          <button
            type="button"
            onClick={openFileDialog}
            disabled={disabled || uploading}
            className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Upload New
          </button>
          <button
            type="button"
            onClick={toggleFileBrowser}
            disabled={disabled || uploading}
            className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Browse Existing
          </button>
        </div>
      )}

      <div
        onClick={showFileBrowser ? undefined : openFileDialog}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`
          relative border-2 border-dashed rounded-lg p-4 text-center transition-all
          ${dragOver 
            ? 'border-blue-400 bg-blue-50' 
            : 'border-gray-300 hover:border-gray-400'
          }
          ${disabled || uploading 
            ? 'opacity-50 cursor-not-allowed' 
            : showFileBrowser ? '' : 'cursor-pointer hover:bg-gray-50'
          }
        `}
      >
        {showPreview && preview ? (
          <div className="relative">
            <img
              src={preview}
              alt="Preview"
              className="max-w-full max-h-24 mx-auto rounded-md"
            />
            {!uploading && !showFileBrowser && (
              <div 
                onClick={openFileDialog}
                className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all rounded-md flex items-center justify-center cursor-pointer"
              >
                <span className="text-white font-medium opacity-0 hover:opacity-100 transition-opacity">
                  Click to change
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className="py-8">
            <div className="w-12 h-12 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              {uploading ? (
                <div className="relative flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              )}
            </div>
            <p className="text-sm text-gray-600 mb-2">
              {uploading ? `Uploading... ${uploadProgress}%` : showFileBrowser ? 'Use buttons above to upload or browse files' : 'Click to upload or drag and drop'}
            </p>
            <p className="text-xs text-gray-500">
              {accept} up to {Math.round(maxSize / 1024 / 1024)}MB
            </p>
          </div>
        )}

        {uploading && (
          <div className="absolute inset-0 bg-white bg-opacity-90 flex flex-col items-center justify-center rounded-lg p-4">
            <div className="w-full max-w-xs">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Uploading...</span>
                <span className="text-sm font-medium text-blue-600">{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 mt-2 text-center">Please wait while your file is being uploaded</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileUpload;